export type Role =
  | "super-admin"
  | "admin"
  | "media-buyer"
  | "supply-chain"
  | "outbound-sales"
  | "user";

export type AppKey =
  | "campaign-creation"
  | "campaign-dashboard"
  | "supply-chain"
  | "outbound"
  | "admin";

/** The app this tool is. Access is checked against it. */
export const THIS_APP: AppKey = "outbound";

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  /** Legacy single role, kept in step with the highest entry in `roles`. */
  role: "user" | "admin" | "super-admin";
  roles?: Role[];
  /**
   * Apps these roles open, computed by the API from one shared access map —
   * this tool checks membership rather than restating the rule.
   */
  apps?: AppKey[];
  created_at: string;
  updated_at: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    token: string;
  };
}

export interface SignupResponse {
  success: boolean;
  message: string;
  data: {
    email: string;
    verification_required: boolean;
  };
}

export interface ResendVerificationResponse {
  success: boolean;
  message: string;
}

export interface ProfileResponse {
  success: boolean;
  data: {
    user: User;
  };
}

export interface TrackingStats {
  total_recovery_emails_sent: number;
  recovered_amount: string;
  orders_completed: number;
  coupons_used: number;
  conversion_rate_percent: string;
  coupon_usage_rate_percent: string;
}

export interface TrackingStatsResponse {
  success: boolean;
  stats: TrackingStats;
}

export interface TrackingRecord {
  id: number;
  order_id: string;
  email: string;
  campaign_id: string | null;
  campaign_name: string | null;
  coupon_code: string | null;
  discount_percent: number;
  total_amount: string | null;
  checkout_url: string | null;
  store_name: string | null;
  emails_sent: number;
  order_status: string;
  order_completed: boolean;
  coupon_used: boolean;
  recovery_sent_at: string;
  order_completed_at: string | null;
  last_status_check_at: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  billing_address1: string | null;
  billing_city: string | null;
  billing_state: string | null;
  billing_country: string | null;
  billing_postal_code: string | null;
  shipping_address1: string | null;
  shipping_city: string | null;
  shipping_state: string | null;
  shipping_country: string | null;
  shipping_postal_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface TrackingRecordsResponse {
  success: boolean;
  records: TrackingRecord[];
  total: number;
  page: number;
  totalPages: number;
  limit: number;
}

export interface CompletionCheckResult {
  success: boolean;
  total_checked: number;
  newly_completed: number;
  coupons_confirmed: number;
  errors: number;
  details: {
    orderId: string;
    status: string;
    completed: boolean;
    couponUsed: boolean;
    error?: string;
  }[];
}

export interface RecoveryFilters {
  email?: string;
  completed?: string;
  coupon_used?: string;
  store_name?: string;
  order_status?: string;
  date_from?: string;
  date_to?: string;
  page: number;
  limit: number;
}

export interface ApiError {
  success: false;
  message: string;
  data?: {
    email?: string;
    verification_required?: boolean;
  };
  errors?: { field: string; message: string }[];
}
